import { ChannelType } from 'src/api/generated';
import type { WhatsAppTemplateDraft } from 'src/modules/shared/messaging/types/whatsapp-template-send.types';
import type { OptionalOptimisticData } from 'src/modules/shared/messaging/types/whatsapp-message-context.type';
import { normalizeWhatsAppPhoneNumber } from 'src/modules/shared/utils/formatting/whatsapp.util';
import type { ConversationWhatsAppTemplateContext } from '../../types/whatsapp/template-send.types';

const samePhone = (selected: string | undefined, submitted: string): boolean =>
  Boolean(
    selected &&
      normalizeWhatsAppPhoneNumber(selected) &&
      normalizeWhatsAppPhoneNumber(selected) ===
        normalizeWhatsAppPhoneNumber(submitted)
  );

/**
 * Captures only identities established for the actual submitted address pair.
 * A contact-only fallback session cannot identify the selected sender's session.
 * @param draft - Neutral editable template form values.
 * @param conversation - Selected CRM address pair and candidate session.
 * @returns Safe local attribution, or no CRM projection for an edited destination.
 */
export const getTemplateAttribution = (
  draft: WhatsAppTemplateDraft,
  conversation: ConversationWhatsAppTemplateContext
): OptionalOptimisticData => {
  if (
    !samePhone(conversation.fromPhoneNumber, draft.fromPhoneNumber) ||
    !samePhone(conversation.toPhoneNumber, draft.toPhoneNumber)
  ) {
    return {};
  }
  const session = conversation.channelSession;
  const matchesSession =
    session?.channelType === ChannelType.WHATSAPP &&
    session.funnelConversationId === conversation.funnelConversationId &&
    session.funnelAddressId === conversation.funnelAddressId &&
    session.contactAddressId === conversation.contactAddressId;
  return {
    contactId: conversation.contactId,
    funnelConversationId: conversation.funnelConversationId,
    funnelId: conversation.funnelId,
    ...(matchesSession
      ? {
          conversationId: session.messagingConversationId ?? undefined,
          sessionId: session.id,
        }
      : {}),
  };
};
